// Street Interview Ad's cast stage (live's `cast-stage` and `street-still-card`; not captured,
// since live's stage needs a paid Approve script, so built from live's code): "Cast & set", then
// the row of the stage, "Location & cast" (the street and the five people) or "Keyframes" (the
// ten start frames and the end card), each still's ↻.
import { Button, Group, Stack, Text } from "@mantine/core";
import { useState } from "react";

import type { StreetDraftStill, StreetDraftStillName, StreetDraftStage } from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { StillCard } from "./StillCard";
import { CAST_STILLS, KEYFRAME_STILLS, stillWaiting } from "./street-draft";
import classes from "./podcast.module.css";

/** Live's labels (`core/constants.ts`, `STILL_LABELS`). */
export const STILL_LABELS: Readonly<Record<StreetDraftStillName, string>> = {
  street: "Street",
  sheet_int: "Interviewer",
  sheet_p1: "P1",
  sheet_p2: "P2",
  sheet_p3: "P3",
  sheet_p4: "P4",
  two_1: "k01 · Question",
  single_2: "k02 · P2",
  single_3: "k03 · P3",
  single_4: "k04 + k09 · P4",
  handover_3: "k05 · Hand-over",
  hold_2: "k06 · P2 reads",
  hold_1: "k07 · P1 holds",
  two_3_hold: "k08 · P3 holds",
  hold_3: "k10 · Converted",
  signoff: "k11 · Sign-off",
  end_card: "End card",
};

const PENDING = { status: "pending", url: null, problem: null } as const;

/** A made still in the shared lightbox (issue 828, bug 25), named by live's label. */
function stillItem(still: StreetDraftStill & { url: string }): PreviewItem {
  return {
    id: still.name,
    name: STILL_LABELS[still.name],
    kind: "image",
    url: still.url,
    thumb: still.url,
    poster: null,
    durationMs: null,
    width: null,
    height: null,
  };
}

export interface StreetCastStageProps {
  stage: StreetDraftStage;
  stills: readonly StreetDraftStill[];
  /** The shot director casts the street: every still waits on it. */
  planning: boolean;
  busy: boolean;
  resetting: boolean;
  onRegenerate: (name: StreetDraftStillName) => void;
  onStartOver: () => void;
}

/**
 * Live's cast stage: "Cast & set", and the stage's row (each still a 9:16 card with its label
 * under it). A done still opens in the shared lightbox (issue 828, bug 25), as Podcast Ad's,
 * stepping through the row's made ones. Ours also offers "Start over" (the draft outlives the
 * panel, where live's closing the panel was its only way back).
 */
export function StreetCastStage(props: StreetCastStageProps) {
  const { stage, stills, planning, busy, resetting, onRegenerate, onStartOver } = props;
  const [preview, setPreview] = useState<StreetDraftStillName | null>(null);
  const [confirming, setConfirming] = useState(false);
  const keyframes = stage === "keyframes";
  const shown = keyframes ? KEYFRAME_STILLS : CAST_STILLS;
  // The row's made stills in live's order: what the lightbox steps through.
  const made = shown.flatMap((name) => {
    const still = stills.find((each) => each.name === name);
    return still?.url ? [{ ...still, url: still.url }] : [];
  });
  return (
    <Stack gap={12} aria-label="Cast & set" component="section">
      <Group justify="space-between" wrap="nowrap">
        <Text className={classes.castTitle}>Cast &amp; set</Text>
        <Button
          variant="subtle"
          size="compact-sm"
          disabled={resetting || busy || planning}
          onClick={() => setConfirming(true)}
        >
          Start over
        </Button>
      </Group>
      <Text className={classes.castLine}>{keyframes ? "Keyframes" : "Location & cast"}</Text>
      <div className={classes.stills}>
        {shown.map((name) => (
          <StillCard
            key={name}
            label={STILL_LABELS[name]}
            still={stills.find((each) => each.name === name) ?? PENDING}
            waiting={planning || stillWaiting(name, stills)}
            busy={busy}
            onRegenerate={() => onRegenerate(name)}
            onFixLabel={null}
            onPreview={() => setPreview(name)}
          />
        ))}
      </div>
      <MediaPreview
        items={made.map(stillItem)}
        current={preview}
        onShow={(name) => setPreview(name as StreetDraftStillName | null)}
      />
      <ConfirmModal
        opened={confirming}
        title="Start over"
        confirmLabel="Start over"
        pending={resetting}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          onStartOver();
          setConfirming(false);
        }}
      >
        <Text size="sm">The script, the cast and the 17 images are cleared, and the form starts empty.</Text>
      </ConfirmModal>
    </Stack>
  );
}
